<script setup lang="ts">
import { isZeroMoney, type Money } from '@taler/domain'
import { ActionButton, Dialog, StatusTag } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  open: boolean
  name: string
  balances: readonly Money[]
  isDeleting: boolean
  failed: boolean
  denied: boolean
  /** The server's own reason, when it sent one. */
  errorDetail: string | undefined
}>()
const emit = defineEmits<{ close: []; confirm: [] }>()
const { t } = useI18n()

const blockedByBalance = computed(() => props.balances.some((balance) => !isZeroMoney(balance)))
const errorMessage = computed(() => {
  if (!props.failed) return undefined
  if (props.denied) return t('contacts.deleteDenied')
  return props.errorDetail ?? t('contacts.deleteErrorBody')
})
</script>

<template>
  <Dialog :open="open" :label="t('contacts.deleteTitle')" @close="emit('close')">
    <div class="flex flex-col gap-[var(--space-group)] p-[var(--space-block)]">
      <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
        {{ t('contacts.deleteConfirmTitle', { name }) }}
      </h2>
      <p class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
        {{ t('contacts.deleteConsequence') }}
      </p>
      <p v-if="blockedByBalance" class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">
        {{ t('contacts.deleteBlockedByBalance') }}
      </p>
      <StatusTag v-if="errorMessage" tone="danger" role="alert">{{ errorMessage }}</StatusTag>
      <div class="flex justify-end gap-[var(--space-inline)]">
        <ActionButton type="button" variant="secondary" @click="emit('close')">
          {{ t('contacts.cancel') }}
        </ActionButton>
        <button
          type="button"
          :disabled="isDeleting || blockedByBalance"
          class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] bg-[color:var(--color-danger)] px-3 py-2 text-[length:var(--text-step-2)] text-[color:var(--color-text-inverse)] disabled:opacity-60"
          @click="emit('confirm')"
        >
          {{ isDeleting ? t('contacts.deleting') : t('contacts.deleteAction') }}
        </button>
      </div>
    </div>
  </Dialog>
</template>
